import type { DatesSetArg, EventDropArg, MoreLinkArg } from "@fullcalendar/core"
import dayjs from "dayjs"
import React, { useEffect, useRef, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { markCalendarNotificationsSeen } from "../../../redux/notificationCounts/notification-counts.thunks.ts"
import { type RootState } from "../../../store.ts"
import { DATE_FORMAT } from "../../../utils/date/date.ts"
import {
  selectActiveBranchId,
  selectIsCompanyMode,
  selectPageFilter
} from "../../Branches/redux/branches.slice.ts"
import { DATE_FORMATS_OBJ } from "../../TeamManagementPage/constants/date-formats.ts"
import { calendarEditAllowedFromPermissions } from "../helpers/calendar-permissions.selectors.ts"
import { useWorkingSchedule } from "../helpers/use-working-schedule.ts"
import { calendarSetProp } from "../redux/calendar.slice.ts"
import {
  fetchEventDetails,
  fetchEvents,
  updateDraggableEvent
} from "../redux/calendar.thunks.ts"
import type {
  CalendarFilters,
  CalendarModalData,
  CalendarState
} from "../redux/calendar.types.ts"
import CalendarPageActions from "./CalendarPageActions.tsx"
import { Calendar } from "./components/Calendar.tsx"
import { rescheduleDroppedEvent } from "./helpers/reschedule-dropped-event.ts"

const CalendarPage = ({
  dateFormat,
  isMobile,
  events,
  filters,
  permissions,
  userInfo,
  selectedEvent,
  setProp,
  fetchEvents,
  getEvent,
  updateDraggableEvent
}: ConnectedProps<typeof connector>) => {
  const prevDateRef = useRef<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const focusDate = searchParams.get("date")
  const focusEventId = searchParams.get("event")
  const focusEditMode = searchParams.get("edit") === "1"
  const openedEventRef = useRef<string | null>(null)
  const dispatch = useAppDispatch()

  // Visiting Calendar is this app's "seen" mechanism for the New Event badge
  // — see docs/kb/decisions/2026-09-12-notification-badges-phase-1.md.
  useEffect(() => {
    dispatch(markCalendarNotificationsSeen())
  }, [dispatch])

  // Calendar Settings > Working Schedule lets a user override the display/
  // scheduling timezone for this page only — the account's own timezone
  // (Settings > Profile) stays the default and the fallback once cleared.
  // Every consumer downstream (FullCalendar, the sidebar, event modals)
  // already reacts to `timezone` changing, so this single override point is
  // enough; no event data is touched, only the zone it's rendered in.
  const [workingSchedule] = useWorkingSchedule()
  const accountTimezone = userInfo?.timezone
  const timezone = workingSchedule.timezone || accountTimezone
  const userId = userInfo?.id
  const moduleAccess = permissions?.codes?.calendar?.calendar
  const moduleFeatures = permissions?.codes?.calendar?.features
  const calendarEditAllowed = calendarEditAllowedFromPermissions(permissions)

  const dayJsDateFormat = dateFormat
    ? DATE_FORMATS_OBJ?.[dateFormat]?.label
    : null

  const handleSetProp = (
    key: keyof CalendarState,
    value: CalendarState[keyof CalendarState]
  ) => setProp({ key, value })

  const handleSetModalData = (data: CalendarModalData | null) =>
    handleSetProp("addModalData", data)

  const handleSetMoreEventsData = (data?: MoreLinkArg) => {
    if (data) {
      handleSetProp("moreEventsData", {
        date: data?.date,
        allSegs: data?.allSegs
      })
    } else {
      handleSetProp("moreEventsData", null)
    }
  }

  // Quick View's own loading/error state — deliberately not the shared
  // `fetching` flag, which also covers the whole-grid fetch. A failed fetch
  // previously left a click with no feedback at all: `fetchEventDetails.
  // rejected` only clears `fetching`, it never surfaces an error. This is
  // the one place an event is opened from (grid click, "+N more", and the
  // deep-link effect below all call it), so the loading/error state covers
  // every entry point rather than just one.
  const [openingEventId, setOpeningEventId] = useState<string | null>(null)
  const [failedEventId, setFailedEventId] = useState<string | null>(null)

  // A deep-linked `?event=&edit=1` is a one-time instruction, not persistent
  // state. Left in the URL, reselecting the same event later (grid click,
  // "+N more") would reopen it in edit mode uninvited — never on open
  // itself, which is racing an in-flight fetch that still needs to read it.
  const clearDeepLinkedEvent = () => {
    if (!focusEventId) return
    openedEventRef.current = null
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("event")
        next.delete("edit")
        return next
      },
      { replace: true }
    )
  }

  // The deep-linked event stops being `selectedEvent` in more ways than
  // CalendarPage's own handlers can enumerate — Save/Delete/Complete
  // dispatch straight into the calendar slice from their thunks, bypassing
  // handleSelectEvent entirely. Reacting to the mismatch itself, rather than
  // calling clearDeepLinkedEvent from every exit by hand, catches all of
  // them at once. Guarded by which id was actually confirmed open so a
  // second deep link followed before the first one's fetch resolves is
  // never mistaken for the first one having "ended".
  const openedViaDeepLinkRef = useRef<string | null>(null)
  useEffect(() => {
    if (!focusEventId) {
      openedViaDeepLinkRef.current = null
      return
    }
    if (selectedEvent?.id === focusEventId) {
      openedViaDeepLinkRef.current = focusEventId
      return
    }
    if (openedViaDeepLinkRef.current === focusEventId) {
      openedViaDeepLinkRef.current = null
      clearDeepLinkedEvent()
    }
  }, [selectedEvent?.id, focusEventId])

  // Cancel inside the edit form is narrower than the above: the event stays
  // open (selectedEvent is unchanged), just no longer in edit mode, so only
  // the edit flag — not the whole deep link — should stop being honored.
  const handleEditCancelled = () => {
    if (!focusEditMode) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("edit")
        return next
      },
      { replace: true }
    )
  }

  const handleSelectEvent = (id: string | null) => {
    if (!id) {
      handleSetProp("selectedEvent", id)
      return
    }

    setFailedEventId(null)
    setOpeningEventId(id)

    getEvent(id)
      .unwrap()
      .catch(() => setFailedEventId(id))
      .finally(() => setOpeningEventId(null))
  }

  const handleRetryEventDetails = () => {
    if (failedEventId) handleSelectEvent(failedEventId)
  }

  // Unlike a close, a failed fetch never reaches the point where the
  // reactive effect above would ever see this event as opened — this is the
  // one exit it structurally cannot catch.
  const handleDismissEventError = () => {
    setFailedEventId(null)
    clearDeepLinkedEvent()
  }

  const handleFetchEvents = (filters: CalendarFilters) => {
    const { participants, ...restFilters } = filters

    fetchEvents({
      ...restFilters,
      page: 1,
      page_size: 100,
      participants: participants?.map((p) => p?.value)?.join(",") || null
    })
  }

  const handleFilterChange = (data: Partial<CalendarFilters>) => {
    handleSetProp("filters", { ...filters, ...data })
    handleFetchEvents({ ...filters, ...data })
  }

  const handleFetchEventsWithDates = async (dates: Partial<DatesSetArg>) => {
    // for some reason, the API token does not have time to install
    // if this is the first request, we wait for the token to install
    if (!prevDateRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 0))
    }

    const newDate = dates.startStr

    if (prevDateRef.current === newDate) return
    if (newDate) prevDateRef.current = newDate

    handleFilterChange({
      start: dayjs(dates.start).add(-1, "day").format(DATE_FORMAT),
      end: dayjs(dates.end).add(1, "day").format(DATE_FORMAT)
    })
  }

  const handleDropEvent = (arg: EventDropArg) =>
    rescheduleDroppedEvent(arg, timezone, (data) =>
      updateDraggableEvent(data).unwrap()
    )

  // useEffect(() => {
  //   isFetching.current = fetching
  // }, [fetching])

  useEffect(() => {
    generateDocumentTitle("Calendar")
    handleSetProp("fetching", false)
  }, [])

  // A meeting opened from a Lead names itself in the URL. Guarded by a ref so
  // re-renders do not reopen a detail the user has already closed.
  useEffect(() => {
    if (!focusEventId || openedEventRef.current === focusEventId) return
    openedEventRef.current = focusEventId
    handleSelectEvent(focusEventId)
  }, [focusEventId])

  // The Branches filter (Company HQ's own multi-select, or the single
  // branch workspace's active branch) lives in a separate slice from this
  // page's own `filters` and is read fresh inside `fetchEvents` on every
  // call — but nothing else here re-triggers a fetch when only THIS
  // changes, since it never flows through `onFilterChange`.
  const isCompanyMode = useAppSelector(selectIsCompanyMode)
  const activeBranchId = useAppSelector(selectActiveBranchId)
  const pageBranchScope = useAppSelector(selectPageFilter("calendar"))
  const pageBranchScopeKey =
    pageBranchScope.type === "subset"
      ? pageBranchScope.teamIds.join(",")
      : "all"

  useEffect(() => {
    if (!prevDateRef.current) return
    handleFetchEvents(filters)
  }, [isCompanyMode, activeBranchId, pageBranchScopeKey])

  return (
    // `#main-content` (the shell's content column) is a plain block element,
    // so two siblings here — this action bar and <Calendar>, which sizes
    // itself to `height: 100%` of its parent expecting the REMAINING space
    // after the bar — would otherwise both fight over the same 100%, and
    // Calendar's own `flex-1 min-h-0` grid never gets a real bounded height
    // to measure against (a percentage height needs a parent with a definite
    // height, not `auto`). This wrapper is that definite-height flex column;
    // <Calendar>'s existing internal layout is untouched.
    <div className="flex h-full min-h-0 flex-col">
      <CalendarPageActions
        dateFormat={dayJsDateFormat}
        userId={userId}
        timezone={timezone}
        permissions={{
          delete: !!permissions?.codes?.calendar?.calendar?.calendar_delete,
          edit: calendarEditAllowed,
          viewLead: !!permissions?.codes?.leads?.leads?.lead_view,
          viewProject: !!permissions?.codes?.projects?.projects?.project_view
        }}
        onSelectEvent={handleSelectEvent}
        openingEventId={openingEventId}
        failedEventId={failedEventId}
        onRetryEventDetails={handleRetryEventDetails}
        onDismissEventError={handleDismissEventError}
        focusEventId={focusEventId}
        focusEditMode={focusEditMode}
        onEditCancelled={handleEditCancelled}
      />
      <Calendar
        timezone={timezone}
        accountTimezone={accountTimezone}
        focusDate={focusDate}
        filters={filters}
        isMobile={isMobile}
        events={events}
        userId={userId}
        //
        createAllowed={moduleAccess?.calendar_create}
        editAllowed={calendarEditAllowed}
        viewAllAllowed={moduleFeatures?.calendar_view_all}
        //
        onSetModalData={handleSetModalData}
        onSelectEvent={handleSelectEvent}
        onSetMoreInfo={handleSetMoreEventsData}
        onFetchEvents={handleFetchEventsWithDates}
        onFilterChange={handleFilterChange}
        onDropEvent={handleDropEvent}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  dateFormat: state.auth.userInfo?.date_format,
  permissions: state.auth.permissions,
  isMobile: state.nav.isMobile,
  events: state.calendar?.events || [],
  filters: state.calendar?.filters,
  userInfo: state.auth.userInfo,
  selectedEvent: state.calendar?.selectedEvent
})

const mapDispatchToProps = {
  fetchEvents: fetchEvents,
  getEvent: fetchEventDetails,
  setProp: calendarSetProp,
  updateDraggableEvent
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(CalendarPage)
